import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPatientDocumentsAction,
  resetPatientDocumentsAction,
} from 'src/redux/actions/documentBundleActions';
import { PatientDocument, PeriodBasis } from '../by-patient/types';

interface Args {
  patientUuid: string | null;
  basis: PeriodBasis;
  intervalIds: string[];
  dateFrom: string | null;
  dateTo: string | null;
  /** SQ-181: include documents under a standing billing hold. */
  includeHeldFromBilling: boolean;
}

/**
 * SQ-162: the document rows themselves.
 *
 * Returning rows rather than a count removes a class of bug — the number on screen is
 * this list's length, so there is no second query that can disagree with it (SQ-156).
 */
export function usePatientDocuments({
  patientUuid,
  basis,
  intervalIds,
  dateFrom,
  dateTo,
  includeHeldFromBilling,
}: Args) {
  const dispatch = useDispatch();
  const { response, loading } = useSelector((state: any) => state.getPatientDocumentsReducer);

  //  Identity, not the array, so a re-render with the same ids does not re-fetch.
  const idKey = intervalIds.join(',');

  useEffect(() => {
    if (!patientUuid) {
      dispatch(resetPatientDocumentsAction());
      return;
    }

    const ids = idKey ? idKey.split(',') : [];
    if (basis !== 'dates' && ids.length === 0) {
      //  An interval basis with nothing ticked means nothing selected — asking the
      //  server would widen it to everything, which is the opposite of what the empty
      //  selection says.
      dispatch(resetPatientDocumentsAction());
      return;
    }

    const params: Record<string, string | string[]> = { basis };
    if (basis === 'dates') {
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
    } else {
      params.interval_id = ids;
    }
    if (includeHeldFromBilling) params.include_held_from_billing = 'true';

    dispatch(getPatientDocumentsAction(patientUuid, params));
  }, [patientUuid, basis, idKey, dateFrom, dateTo, includeHeldFromBilling, dispatch]);

  //  SQ-181: the response is an object now, not a bare list. The server filters and
  //  reports in one answer, so the list and the "+N held" beside it cannot disagree —
  //  the browser is handed what to show, not what to show plus instructions.
  const documents: PatientDocument[] = useMemo(() => response?.items ?? [], [response]);
  const heldWithheldCount: number = response?.heldWithheldCount ?? 0;

  return { documents, loading, heldWithheldCount };
}
